import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBranch } from "../../../layouts/BranchContext";
import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpenOutlined";

/* ==========================================================================
   CONSTANTS
   ========================================================================== */

// API value -> display label
const STATUS_LABELS = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
};
const STATUS_VALUES = Object.keys(STATUS_LABELS);
const STATUS_COLORS = {
  not_started: "default",
  in_progress: "warning",
  completed: "success",
};

const getToday = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const EMPTY_FILTERS = {
  search: "",
  moduleId: "",
  status: "",
  dateFrom: "",
  dateTo: "",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]; // API allows 1-100

/* ==========================================================================
   API LAYER  (all endpoints from the Course Tracker integration contract)
   Tenant (organization / branch) always comes from the JWT claims, so it is
   never sent from the frontend.
   ========================================================================== */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/course/course-trackers`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const asList = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];

// Remove empty params so we never send "module=" etc.
const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== "" && v !== null && v !== undefined
    )
  );

const trackerApi = {
  fetchTrainers: async () => asList((await api.get("/trainers/")).data),

  fetchCourses: async ({ trainer }) =>
    asList((await api.get("/courses/", { params: { trainer } })).data),

  fetchBatches: async ({ trainer, course }) => {
    const { data } = await api.get("/batches/", { params: { trainer, course } });
    return { list: asList(data), message: data?.message || null };
  },

  fetchModules: async ({ course }) =>
    asList((await api.get("/modules/", { params: { course } })).data),

  // Returns { selection, summary, count, page, page_size, results }
  fetchTopics: async (params) =>
    (await api.get("/topics/", { params: clean(params) })).data,

  // Walks every page (page_size 100) and returns all rows.
  fetchAllTopics: async (params) => {
    let page = 1;
    let all = [];
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const data = await trackerApi.fetchTopics({
        ...params,
        page,
        page_size: 100,
      });
      all = all.concat(data.results || []);
      if (all.length >= (data.count || 0) || !(data.results || []).length) {
        return all;
      }
      page += 1;
    }
  },

  bulkSave: async (body) => (await api.post("/bulk-save/", body)).data,
  addEntries: async (body) => (await api.post("/add/", body)).data,
  handover: async (body) => (await api.post("/handover/", body)).data,
};

/* ---------- error helper ---------- */

const firstText = (v) => (Array.isArray(v) ? v.join(", ") : String(v));

// Turns any API error into { status, message, rowErrors }
const describeError = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const info = { status, message: fallback, rowErrors: [] };

  if (!err?.response) {
    info.message = "Network error. Check your connection and try again.";
    return info;
  }
  if (status === 401) {
    info.message = "Your session has expired. Please login again.";
    return info;
  }
  if (status === 403) {
    info.message = data?.detail || "Access denied for this action.";
    return info;
  }
  if (status === 404) {
    info.message =
      data?.detail ||
      "Selected data is inactive or outside your scope. Please reselect.";
    return info;
  }
  if (typeof data === "string") {
    info.message = data;
    return info;
  }
  if (data && typeof data === "object") {
    if (Array.isArray(data.row_errors) && data.row_errors.length) {
      info.rowErrors = data.row_errors;
      const first = data.row_errors[0];
      const msg = Object.values(first.errors || {}).map(firstText)[0];
      info.message = `Fix ${data.row_errors.length} highlighted row(s) and save again${
        msg ? `: ${msg}` : ""
      }`;
      return info;
    }
    if (data.detail) info.message = data.detail;
    else if (data.message) info.message = data.message;
    else {
      const key = Object.keys(data)[0];
      if (key) info.message = firstText(data[key]);
    }
  }
  return info;
};

/* ==========================================================================
   SMALL PIECES
   ========================================================================== */

const StatusChip = ({ status }) => (
  <Chip
    size="small"
    label={STATUS_LABELS[status] || status}
    color={STATUS_COLORS[status] || "default"}
    sx={{ fontWeight: 600 }}
  />
);

const DialogHeader = ({ title, subtitle, onClose }) => (
  <DialogTitle sx={{ pr: 7 }}>
    <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
    <IconButton
      onClick={onClose}
      sx={{ position: "absolute", right: 12, top: 12 }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
);

/* ---------- Remark dialog ---------- */

const RemarkDialog = ({ row, onClose, onSave }) => {
  const [text, setText] = useState("");
  useEffect(() => {
    setText(row?.remark || "");
  }, [row]);

  return (
    <Dialog open={!!row} onClose={onClose} fullWidth maxWidth="xs">
      <DialogHeader
        title={row ? `Remark for ${row.topic_name}` : "Remark"}
        onClose={onClose}
      />
      <DialogContent>
        <TextField
          autoFocus
          multiline
          rows={3}
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type your remark here..."
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onSave(text)}>
          Save remark
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------- Add tracker dialog ---------- */

const AddTrackerDialog = ({
  open,
  onClose,
  selection,
  trainer,
  course,
  batch,
  modules,
  onSaved,
  notify,
  onError,
}) => {
  const [moduleId, setModuleId] = useState("");
  const [topicIds, setTopicIds] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [date, setDate] = useState(getToday());
  const [status, setStatus] = useState("in_progress");
  const [videoUrl, setVideoUrl] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setModuleId("");
      setTopicIds([]);
      setAvailable([]);
      setDate(getToday());
      setStatus("in_progress");
      setVideoUrl("");
      setRemark("");
    }
  }, [open]);

  // Topics of the chosen module that were never saved for this batch
  // (id === null) are the ones that can still be added.
  useEffect(() => {
    if (!open || !moduleId) {
      setAvailable([]);
      return;
    }
    let cancelled = false;
    setLoadingTopics(true);
    trackerApi
      .fetchAllTopics({
        trainer: selection.trainerId,
        course: selection.courseId,
        batch: selection.batchId,
        module: moduleId,
      })
      .then((rows) => {
        if (!cancelled) setAvailable(rows.filter((r) => r.id === null));
      })
      .catch((err) => {
        if (!cancelled) {
          setAvailable([]);
          onError(err, "Failed to load topics");
        }
      })
      .finally(() => !cancelled && setLoadingTopics(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, moduleId, selection.trainerId, selection.courseId, selection.batchId]);

  const needsDate = status !== "not_started";
  const dateFuture = status === "completed" && date > getToday();

  const handleSave = async () => {
    if (needsDate && !date) {
      notify("Date is required for In Progress and Completed topics", "warning");
      return;
    }
    if (dateFuture) {
      notify("Completed date cannot be in the future", "warning");
      return;
    }
    setSaving(true);
    try {
      const res = await trackerApi.addEntries({
        trainer: Number(selection.trainerId),
        course: Number(selection.courseId),
        batch: Number(selection.batchId),
        module: Number(moduleId),
        topics: topicIds,
        status,
        date: needsDate ? date : null,
        video_url: videoUrl.trim() || null,
        remark: remark || "",
      });
      notify(`${res.saved_count ?? topicIds.length} topic(s) added`, "success");
      onSaved();
      onClose();
    } catch (err) {
      onError(err, "Failed to add entries");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogHeader
        title="Add course tracker entry"
        subtitle="Record topics taught by this trainer for this batch."
        onClose={onClose}
      />
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
          <TextField
            label="Trainer"
            value={trainer?.name || ""}
            disabled
            sx={{ flex: "1 1 30%" }}
          />
          <TextField
            label="Course"
            value={course?.course_name || ""}
            disabled
            sx={{ flex: "1 1 30%" }}
          />
          <TextField
            label="Batch"
            value={batch ? `${batch.batch_code} - ${batch.batch_name}` : ""}
            disabled
            sx={{ flex: "1 1 30%" }}
          />

          <FormControl sx={{ flex: "1 1 45%" }}>
            <InputLabel>Module *</InputLabel>
            <Select
              label="Module *"
              value={moduleId}
              onChange={(e) => {
                setModuleId(e.target.value);
                setTopicIds([]);
              }}
            >
              {modules.length === 0 && (
                <MenuItem disabled>No modules in this course</MenuItem>
              )}
              {modules.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.module_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: "1 1 45%" }} disabled={!moduleId}>
            <InputLabel>Topics *</InputLabel>
            <Select
              multiple
              label="Topics *"
              value={topicIds}
              onChange={(e) => setTopicIds(e.target.value)}
              renderValue={(sel) =>
                available
                  .filter((t) => sel.includes(t.topic))
                  .map((t) => t.topic_name)
                  .join(", ")
              }
            >
              {loadingTopics && <MenuItem disabled>Loading topics...</MenuItem>}
              {!loadingTopics && available.length === 0 && (
                <MenuItem disabled>
                  {moduleId
                    ? "All topics of this module are already recorded"
                    : "Select a module first"}
                </MenuItem>
              )}
              {available.map((t) => (
                <MenuItem key={t.topic} value={t.topic}>
                  <Checkbox checked={topicIds.includes(t.topic)} />
                  <ListItemText primary={t.topic_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: "1 1 45%" }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_VALUES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={needsDate ? "Date *" : "Date"}
            type="date"
            value={needsDate ? date : ""}
            disabled={!needsDate}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={status === "completed" ? { max: getToday() } : undefined}
            error={dateFuture}
            helperText={dateFuture ? "Cannot be a future date" : ""}
            sx={{ flex: "1 1 45%" }}
          />

          <TextField
            label="Video URL"
            fullWidth
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://"
          />

          <TextField
            label="Remark"
            multiline
            rows={3}
            fullWidth
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Enter remarks"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !moduleId || topicIds.length === 0}
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {saving ? "Saving..." : "Save entries"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------- Handover dialog (admin / branch admin only) ---------- */

const HandoverDialog = ({
  open,
  onClose,
  selection,
  trainer,
  course,
  batch,
  trainers,
  onDone,
  notify,
  onError,
}) => {
  const [toTrainer, setToTrainer] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(getToday());
  const [scope, setScope] = useState("all");
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setToTrainer("");
    setEffectiveDate(getToday());
    setScope("all");
    setChosen([]);
    setRemark("");
    setPending([]);
    setLoadingPending(true);
    trackerApi
      .fetchAllTopics({
        trainer: selection.trainerId,
        course: selection.courseId,
        batch: selection.batchId,
      })
      .then((rows) => {
        if (!cancelled) setPending(rows.filter((r) => r.status !== "completed"));
      })
      .catch((err) => !cancelled && onError(err, "Failed to load topics"))
      .finally(() => !cancelled && setLoadingPending(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selection.trainerId, selection.courseId, selection.batchId]);

  const toggle = (id) =>
    setChosen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const transferCount = scope === "all" ? pending.length : chosen.length;

  const handleConfirm = async () => {
    if (!effectiveDate) {
      notify("Effective date is required", "warning");
      return;
    }
    setSaving(true);
    try {
      const body = {
        from_trainer: Number(selection.trainerId),
        to_trainer: Number(toTrainer),
        course: Number(selection.courseId),
        batch: Number(selection.batchId),
        effective_date: effectiveDate,
        all_not_completed: scope === "all",
        remark: remark || "",
      };
      if (scope !== "all") body.topics = chosen;
      const res = await trackerApi.handover(body);
      notify(
        `${res.transferred_count ?? transferCount} topic(s) handed over`,
        "success"
      );
      onDone();
      onClose();
    } catch (err) {
      onError(err, "Handover failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogHeader
        title="Trainer handover"
        subtitle="Transfer the remaining topics of this batch to a new trainer."
        onClose={onClose}
      />
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
          <TextField
            label="From trainer"
            value={trainer?.name || ""}
            disabled
            sx={{ flex: "1 1 45%" }}
          />
          <FormControl sx={{ flex: "1 1 45%" }}>
            <InputLabel>To trainer *</InputLabel>
            <Select
              label="To trainer *"
              value={toTrainer}
              onChange={(e) => setToTrainer(e.target.value)}
            >
              {trainers
                .filter((t) => t.id !== Number(selection.trainerId))
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} ({t.employee_code})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label="Course"
            value={course?.course_name || ""}
            disabled
            sx={{ flex: "1 1 30%" }}
          />
          <TextField
            label="Batch"
            value={batch ? `${batch.batch_code} - ${batch.batch_name}` : ""}
            disabled
            sx={{ flex: "1 1 30%" }}
          />
          <TextField
            label="Effective date *"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: "1 1 30%" }}
          />
        </Box>

        <Typography sx={{ fontWeight: 600, mt: 2 }}>
          Topics to transfer
        </Typography>
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
          <FormControlLabel
            value="all"
            control={<Radio />}
            label={
              loadingPending
                ? "All topics not yet completed (loading...)"
                : `All topics not yet completed (${pending.length})`
            }
          />
          <FormControlLabel
            value="choose"
            control={<Radio />}
            label="Choose topics"
          />
        </RadioGroup>

        {scope === "choose" && (
          <Paper
            variant="outlined"
            sx={{ maxHeight: 180, overflowY: "auto", p: 1, mb: 1 }}
          >
            {pending.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                {loadingPending ? "Loading..." : "No remaining topics."}
              </Typography>
            )}
            {pending.map((r) => (
              <FormControlLabel
                key={r.topic}
                sx={{ display: "flex" }}
                control={
                  <Checkbox
                    checked={chosen.includes(r.topic)}
                    onChange={() => toggle(r.topic)}
                  />
                }
                label={`${r.module_name} - ${r.topic_name}`}
              />
            ))}
          </Paper>
        )}

        <MuiAlert severity="info" sx={{ my: 1.5 }}>
          Completed topics are never transferred. The previous trainer&apos;s
          rows stay as history and the new trainer gets fresh Not Started rows.
          Only an administrator can hand over topics.
        </MuiAlert>

        <TextField
          label="Remark"
          multiline
          rows={2}
          fullWidth
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Reason for handover (optional)"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || loadingPending || !toTrainer || transferCount === 0}
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {saving ? "Transferring..." : "Confirm handover"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------- User guide ---------- */

const GuideDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const Heading = ({ children }) => (
    <Typography
      sx={{ fontWeight: 700, color: theme.palette.primary.main, mt: 2, mb: 0.5 }}
    >
      {children}
    </Typography>
  );
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogHeader title="Course tracker - user guide" onClose={onClose} />
      <DialogContent>
        <Heading>View topics</Heading>
        <ul style={{ lineHeight: 1.8, marginTop: 0 }}>
          <li>Select the <b>Trainer</b>, then the <b>Course</b>, then the <b>Batch</b>.</li>
          <li>The trainer&apos;s topics appear with their module, date and status.</li>
          <li>Topics nobody has started yet also appear as Not Started.</li>
          <li>Narrow the list with search, module, status and the date range.</li>
        </ul>
        <Heading>Update progress</Heading>
        <ul style={{ lineHeight: 1.8, marginTop: 0 }}>
          <li>Change the date, status, video URL or remark. The row is checked and highlighted automatically.</li>
          <li>In Progress and Completed topics need a date. Completed cannot be a future date.</li>
          <li>Not Started clears the date.</li>
          <li>Edits are kept while you change pages or filters. Click <b>Save changes</b> to store every checked row together.</li>
        </ul>
        <Heading>Add tracker</Heading>
        <ul style={{ lineHeight: 1.8, marginTop: 0 }}>
          <li>Use it to record topics that were taught but are not yet tracked.</li>
          <li>Pick a module, then one or more topics, a status and a date.</li>
        </ul>
        <Heading>Handover (administrators)</Heading>
        <ul style={{ lineHeight: 1.8, marginTop: 0 }}>
          <li>Use it when a trainer is replaced in the middle of a course.</li>
          <li>Choose the new trainer, the effective date and which topics move.</li>
          <li>Completed topics stay with the earlier trainer, so history is preserved.</li>
        </ul>
      </DialogContent>
    </Dialog>
  );
};

/* ==========================================================================
   MAIN PAGE
   ========================================================================== */

const CourseTracker = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedBranchId, branchList } = useBranch();

  const branchName =
    (branchList || []).find((b) => String(b.id) === String(selectedBranchId))
      ?.name || "";

  /* ---------- state ---------- */
  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchMessage, setBatchMessage] = useState(null);
  const [modules, setModules] = useState([]);

  const [selection, setSelection] = useState({
    trainerId: "",
    courseId: "",
    batchId: "",
  });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState("");

  const [rows, setRows] = useState([]); // current server page
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local edits survive paging / filtering: { [topicId]: {date,status,...} }
  const [edits, setEdits] = useState({});
  const [checked, setChecked] = useState({}); // { [topicId]: true }
  const [rowErrors, setRowErrors] = useState({}); // { [topicId]: "message" }

  const [page, setPage] = useState(0); // 0-based for MUI, API is 1-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [remarkRow, setRemarkRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const notify = useCallback(
    (message, severity = "success") =>
      setSnack({ open: true, message, severity }),
    []
  );

  // Shared API error handler: 401 -> login, everything else -> snackbar.
  const reportError = useCallback(
    (err, fallback) => {
      const info = describeError(err, fallback);
      notify(info.message, "error");
      if (info.status === 401) {
        setTimeout(() => navigate("/login"), 1500);
      }
      return info;
    },
    [notify, navigate]
  );

  /* ---------- derived ---------- */
  const ready = selection.trainerId && selection.courseId && selection.batchId;
  const editCount = Object.keys(edits).length;
  const hasUnsaved = editCount > 0;
  const checkedCount = Object.keys(checked).length;
  const dateRangeInvalid =
    filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo;
  const filtersActive =
    !!searchInput ||
    !!filters.moduleId ||
    !!filters.status ||
    !!filters.dateFrom ||
    !!filters.dateTo;

  const trainer = trainers.find((t) => t.id === Number(selection.trainerId));
  const course = courses.find((c) => c.id === Number(selection.courseId));
  const batch = batches.find((b) => b.id === Number(selection.batchId));

  // Server row merged with the user's local edits.
  const displayRows = rows.map((r) => ({ ...r, ...(edits[r.topic] || {}) }));

  const pageAllChecked =
    displayRows.length > 0 && displayRows.every((r) => checked[r.topic]);
  const pageSomeChecked = displayRows.some((r) => checked[r.topic]);

  /* ---------- guard for unsaved edits ---------- */
  const guard = (action) => {
    if (hasUnsaved) setPendingAction(() => action);
    else action();
  };

  const clearEdits = () => {
    setEdits({});
    setChecked({});
    setRowErrors({});
  };

  /* ---------- selector loaders ---------- */

  // Trainers (re-fetched when the branch changes in the navbar)
  useEffect(() => {
    let cancelled = false;
    trackerApi
      .fetchTrainers()
      .then((list) => {
        if (cancelled) return;
        setTrainers(list);
        // A trainer account usually sees only themselves: select them.
        if (list.length === 1) {
          setSelection((s) =>
            s.trainerId ? s : { trainerId: list[0].id, courseId: "", batchId: "" }
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTrainers([]);
          reportError(err, "Failed to load trainers");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBranchId, reportError]);

  // Courses for the selected trainer
  useEffect(() => {
    if (!selection.trainerId) {
      setCourses([]);
      return;
    }
    let cancelled = false;
    trackerApi
      .fetchCourses({ trainer: selection.trainerId })
      .then((list) => !cancelled && setCourses(list))
      .catch((err) => {
        if (!cancelled) {
          setCourses([]);
          reportError(err, "Failed to load courses");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selection.trainerId, reportError]);

  // Batches for trainer + course
  useEffect(() => {
    if (!selection.trainerId || !selection.courseId) {
      setBatches([]);
      setBatchMessage(null);
      return;
    }
    let cancelled = false;
    trackerApi
      .fetchBatches({
        trainer: selection.trainerId,
        course: selection.courseId,
      })
      .then(({ list, message }) => {
        if (cancelled) return;
        setBatches(list);
        setBatchMessage(message);
      })
      .catch((err) => {
        if (!cancelled) {
          setBatches([]);
          reportError(err, "Failed to load batches");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selection.trainerId, selection.courseId, reportError]);

  // Modules are needed once the course is chosen (filter + Add dialog)
  useEffect(() => {
    if (!selection.courseId) {
      setModules([]);
      return;
    }
    let cancelled = false;
    trackerApi
      .fetchModules({ course: selection.courseId })
      .then((list) => !cancelled && setModules(list))
      .catch((err) => {
        if (!cancelled) {
          setModules([]);
          reportError(err, "Failed to load modules");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selection.courseId, reportError]);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => {
      const v = searchInput.trim();
      setFilters((f) => (f.search === v ? f : { ...f, search: v }));
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ---------- table loader ---------- */
  const requestId = useRef(0);

  const loadRows = useCallback(async () => {
    if (!ready || dateRangeInvalid) {
      setRows([]);
      setTotalCount(0);
      return;
    }
    const myRequest = ++requestId.current;
    setLoadingRows(true);
    try {
      const data = await trackerApi.fetchTopics({
        trainer: selection.trainerId,
        course: selection.courseId,
        batch: selection.batchId,
        module: filters.moduleId,
        status: filters.status,
        search: filters.search,
        start_date: filters.dateFrom,
        end_date: filters.dateTo,
        page: page + 1,
        page_size: rowsPerPage,
      });
      if (myRequest !== requestId.current) return; // stale response
      const count = data.count || 0;
      // Page no longer exists (rows were removed): jump to the last page.
      if (!(data.results || []).length && page > 0 && count > 0) {
        setPage(Math.max(0, Math.ceil(count / rowsPerPage) - 1));
        return;
      }
      setRows(data.results || []);
      setTotalCount(count);
      setSummary(data.summary || null);
    } catch (err) {
      if (myRequest !== requestId.current) return;
      const info = reportError(err, "Failed to load tracking data");
      setRows([]);
      setTotalCount(0);
      // 404 = selection is inactive / out of scope -> reset the selectors
      if (info.status === 404) {
        setSelection((s) => ({ ...s, batchId: "" }));
      }
    } finally {
      if (myRequest === requestId.current) setLoadingRows(false);
    }
  }, [
    ready,
    dateRangeInvalid,
    selection,
    filters,
    page,
    rowsPerPage,
    reportError,
  ]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Branch change from the navbar resets everything.
  useEffect(() => {
    setSelection({ trainerId: "", courseId: "", batchId: "" });
    setFilters(EMPTY_FILTERS);
    setSearchInput("");
    setRows([]);
    setSummary(null);
    setPage(0);
    clearEdits();
  }, [selectedBranchId]);

  /* ---------- selection handlers ---------- */
  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput("");
    setPage(0);
  };

  const onTrainer = (v) =>
    guard(() => {
      setSelection({ trainerId: v, courseId: "", batchId: "" });
      resetFilters();
      clearEdits();
      setSummary(null);
    });
  const onCourse = (v) =>
    guard(() => {
      setSelection((s) => ({ ...s, courseId: v, batchId: "" }));
      resetFilters();
      clearEdits();
      setSummary(null);
    });
  const onBatch = (v) =>
    guard(() => {
      setSelection((s) => ({ ...s, batchId: v }));
      resetFilters();
      clearEdits();
    });

  // Edits are kept across filters, so no guard is needed here.
  const onFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };

  /* ---------- row editing ---------- */
  const updateRow = (topicId, patch) => {
    const base = rows.find((r) => r.topic === topicId);
    if (!base) return;
    setEdits((prev) => {
      const current = { ...base, ...(prev[topicId] || {}) };
      const next = { ...current, ...patch };
      if (patch.status === "not_started") {
        next.date = ""; // Not Started clears the date
      } else if (patch.status && !next.date) {
        next.date = getToday();
      }
      return { ...prev, [topicId]: next };
    });
    setChecked((prev) => ({ ...prev, [topicId]: true }));
    setRowErrors((prev) => {
      if (!prev[topicId]) return prev;
      const copy = { ...prev };
      delete copy[topicId];
      return copy;
    });
  };

  const toggleRow = (topicId) =>
    setChecked((prev) => {
      const copy = { ...prev };
      if (copy[topicId]) delete copy[topicId];
      else copy[topicId] = true;
      return copy;
    });

  const togglePage = () =>
    setChecked((prev) => {
      const copy = { ...prev };
      displayRows.forEach((r) => {
        if (pageAllChecked) delete copy[r.topic];
        else copy[r.topic] = true;
      });
      return copy;
    });

  const discardEdits = () => {
    clearEdits();
  };

  /* ---------- save ---------- */
  const handleSave = async () => {
    const ids = Object.keys(checked).map(Number);
    if (ids.length === 0) {
      notify("Select at least one row to save", "warning");
      return;
    }

    // Selected rows: local edit if present, else the row as loaded.
    const rowByTopic = new Map(rows.map((r) => [r.topic, r]));
    const selected = ids
      .map((id) => edits[id] || rowByTopic.get(id))
      .filter(Boolean);

    const today = getToday();
    const missingDate = selected.find(
      (r) => r.status !== "not_started" && !r.date
    );
    if (missingDate) {
      notify(`Add a date for "${missingDate.topic_name}" before saving`, "warning");
      return;
    }
    const future = selected.find(
      (r) => r.status === "completed" && r.date > today
    );
    if (future) {
      notify(
        `"${future.topic_name}" cannot be completed on a future date`,
        "warning"
      );
      return;
    }

    setSaving(true);
    setRowErrors({});
    try {
      const res = await trackerApi.bulkSave({
        trainer: Number(selection.trainerId),
        course: Number(selection.courseId),
        batch: Number(selection.batchId),
        rows: selected.map((r) => ({
          topic: r.topic,
          status: r.status,
          date: r.status === "not_started" ? null : r.date || null,
          video_url: (r.video_url || "").trim() || null,
          remark: r.remark || "",
        })),
      });
      notify(`${res.saved_count ?? selected.length} topic(s) saved`, "success");
      clearEdits();
      await loadRows(); // reload to get server truth and new summary
    } catch (err) {
      const info = reportError(err, "Failed to save changes");
      // Bulk save is atomic: keep every local edit, mark the failing rows.
      if (info.rowErrors.length) {
        const map = {};
        info.rowErrors.forEach((re) => {
          const topicId = re.topic ?? selected[re.row]?.topic;
          if (topicId !== undefined && topicId !== null) {
            map[topicId] = Object.values(re.errors || {})
              .map(firstText)
              .join(", ");
          }
        });
        setRowErrors(map);
      }
    } finally {
      setSaving(false);
    }
  };

  /* ---------- styles ---------- */
  const cardSx = { p: 2, borderRadius: "16px", mb: 2 };
  const dirtyBg = alpha(theme.palette.warning.main, 0.14);
  const errorBg = alpha(theme.palette.error.main, 0.12);

  /* ======================================================================
     RENDER
     ====================================================================== */

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* ---------- header ---------- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard")}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Back
        </Button>

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Course tracker
          </Typography>
          {branchName && (
            <Typography variant="body2" color="text.secondary">
              Branch: {branchName}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!ready}
          onClick={() => setAddOpen(true)}
          sx={{ borderRadius: "10px", height: 44, fontWeight: 700 }}
        >
          Add tracker
        </Button>
        <Button
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          disabled={!ready}
          onClick={() => guard(() => { clearEdits(); setHandoverOpen(true); })}
          sx={{ borderRadius: "10px", height: 44, fontWeight: 700 }}
        >
          Handover
        </Button>
        <IconButton onClick={() => setGuideOpen(true)} aria-label="User guide">
          <InfoOutlineIcon sx={{ fontSize: 30 }} />
        </IconButton>
      </Box>

      {/* ---------- selectors ---------- */}
      <Paper elevation={3} sx={cardSx}>
        <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
          Select trainer, course and batch
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl sx={{ flex: "1 1 240px" }}>
            <InputLabel>Trainer *</InputLabel>
            <Select
              label="Trainer *"
              value={selection.trainerId}
              onChange={(e) => onTrainer(e.target.value)}
            >
              {trainers.length === 0 && (
                <MenuItem disabled>No trainers found</MenuItem>
              )}
              {trainers.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.employee_code} - {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: "1 1 240px" }} disabled={!selection.trainerId}>
            <InputLabel>Course *</InputLabel>
            <Select
              label="Course *"
              value={selection.courseId}
              onChange={(e) => onCourse(e.target.value)}
            >
              {courses.length === 0 && (
                <MenuItem disabled>No courses for this trainer</MenuItem>
              )}
              {courses.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.course_name}
                  {c.course_code ? ` (${c.course_code})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: "1 1 240px" }} disabled={!selection.courseId}>
            <InputLabel>Batch *</InputLabel>
            <Select
              label="Batch *"
              value={selection.batchId}
              onChange={(e) => onBatch(e.target.value)}
            >
              {batches.length === 0 && (
                <MenuItem disabled>
                  {batchMessage || "No batches assigned"}
                </MenuItem>
              )}
              {batches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.batch_code} - {b.batch_name}
                  {b.batch_time ? ` (${b.batch_time})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* ---------- empty state ---------- */}
      {!ready && (
        <Paper elevation={3} sx={{ ...cardSx, textAlign: "center", py: 7 }}>
          <FolderOpenIcon sx={{ fontSize: 56, color: "text.secondary" }} />
          <Typography sx={{ fontWeight: 600, mt: 1 }}>
            Select a trainer, course and batch to view topics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Topics assigned to the chosen trainer will appear here.
          </Typography>
        </Paper>
      )}

      {ready && (
        <>
          {/* ---------- progress ---------- */}
          {summary && (
            <Paper elevation={3} sx={cardSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>Batch progress</Typography>
                <Chip label={`Not started ${summary.not_started}`} />
                <Chip color="warning" label={`In progress ${summary.in_progress}`} />
                <Chip color="success" label={`Completed ${summary.completed}`} />
                <Box
                  sx={{
                    flex: "1 1 200px",
                    height: 12,
                    borderRadius: 6,
                    overflow: "hidden",
                    display: "flex",
                    bgcolor: theme.palette.action.hover,
                  }}
                >
                  <Box
                    sx={{
                      width: `${summary.total ? (summary.completed / summary.total) * 100 : 0}%`,
                      bgcolor: theme.palette.success.main,
                    }}
                  />
                  <Box
                    sx={{
                      width: `${summary.total ? (summary.in_progress / summary.total) * 100 : 0}%`,
                      bgcolor: theme.palette.warning.main,
                    }}
                  />
                </Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {summary.completion_percentage}%
                </Typography>
              </Box>
            </Paper>
          )}

          {/* ---------- filters ---------- */}
          <Paper elevation={3} sx={cardSx}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                placeholder="Search topic or module..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                sx={{
                  flex: "1 1 260px",
                  "& .MuiOutlinedInput-root": { borderRadius: "30px" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchInput("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <FormControl sx={{ minWidth: 170 }} size="small">
                <InputLabel>Module</InputLabel>
                <Select
                  label="Module"
                  value={filters.moduleId}
                  onChange={(e) => onFilter({ moduleId: e.target.value })}
                >
                  <MenuItem value="">All modules</MenuItem>
                  {modules.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.module_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 160 }} size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => onFilter({ status: e.target.value })}
                >
                  <MenuItem value="">All status</MenuItem>
                  {STATUS_VALUES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="date"
                label="From"
                value={filters.dateFrom}
                onChange={(e) => onFilter({ dateFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!dateRangeInvalid}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={filters.dateTo}
                onChange={(e) => onFilter({ dateTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!dateRangeInvalid}
                helperText={dateRangeInvalid ? "To must be after From" : ""}
              />

              <Button color="error" onClick={resetFilters} disabled={!filtersActive}>
                Clear filters
              </Button>
            </Box>
            {filters.dateFrom || filters.dateTo ? (
              <Typography variant="caption" color="text.secondary">
                Date filters only show topics that already have a date.
              </Typography>
            ) : null}
          </Paper>

          {/* ---------- table ---------- */}
          <Paper elevation={3} sx={{ borderRadius: "16px", overflow: "hidden" }}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table
                sx={{
                  minWidth: 980,
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    fontWeight: "bold",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={pageAllChecked}
                        indeterminate={!pageAllChecked && pageSomeChecked}
                        onChange={togglePage}
                        sx={{
                          color: "white",
                          "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                            color: "white",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>S.No</TableCell>
                    <TableCell>Module</TableCell>
                    <TableCell>Topic</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Video URL</TableCell>
                    <TableCell align="center">Remark</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loadingRows ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : displayRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        {!filtersActive && totalCount === 0
                          ? "No topics added to this course."
                          : "No topics match the selected filters."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayRows.map((row, index) => {
                      const dirty = !!edits[row.topic];
                      const err = rowErrors[row.topic];
                      return (
                        <TableRow
                          key={row.topic}
                          hover
                          sx={
                            err
                              ? { backgroundColor: errorBg }
                              : dirty
                              ? { backgroundColor: dirtyBg }
                              : undefined
                          }
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={!!checked[row.topic]}
                              onChange={() => toggleRow(row.topic)}
                            />
                          </TableCell>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>{row.module_name}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {row.topic_name}
                            {err && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ display: "block" }}
                              >
                                {err}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <TextField
                              type="date"
                              size="small"
                              value={row.date || ""}
                              disabled={row.status === "not_started"}
                              onChange={(e) =>
                                updateRow(row.topic, { date: e.target.value })
                              }
                              inputProps={
                                row.status === "completed"
                                  ? { max: getToday() }
                                  : undefined
                              }
                              sx={{ minWidth: 150 }}
                            />
                          </TableCell>

                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <Select
                                value={row.status}
                                onChange={(e) =>
                                  updateRow(row.topic, { status: e.target.value })
                                }
                                renderValue={(v) => <StatusChip status={v} />}
                              >
                                {STATUS_VALUES.map((s) => (
                                  <MenuItem key={s} value={s}>
                                    <StatusChip status={s} />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>

                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="https://"
                              value={row.video_url || ""}
                              onChange={(e) =>
                                updateRow(row.topic, { video_url: e.target.value })
                              }
                              sx={{ minWidth: 170 }}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip
                              title={row.remark ? row.remark : "Add or edit remark"}
                            >
                              <IconButton
                                size="small"
                                color={row.remark ? "primary" : "default"}
                                onClick={() => setRemarkRow(row)}
                                sx={{ backgroundColor: theme.palette.action.hover }}
                              >
                                <NoteAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                px: 2,
                py: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip
                  title={checkedCount === 0 ? "Select at least one row" : ""}
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving || checkedCount === 0}
                      startIcon={
                        saving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {saving
                        ? "Saving..."
                        : `Save changes${checkedCount ? ` (${checkedCount})` : ""}`}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={!hasUnsaved || saving}
                  onClick={discardEdits}
                >
                  Discard edits
                </Button>
              </Box>

              <TablePagination
                component="div"
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ border: 0 }}
              />
            </Box>
          </Paper>
        </>
      )}

      {/* ---------- dialogs ---------- */}
      <RemarkDialog
        row={remarkRow}
        onClose={() => setRemarkRow(null)}
        onSave={(text) => {
          updateRow(remarkRow.topic, { remark: text });
          setRemarkRow(null);
        }}
      />

      <AddTrackerDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        selection={selection}
        trainer={trainer}
        course={course}
        batch={batch}
        modules={modules}
        onSaved={loadRows}
        notify={notify}
        onError={reportError}
      />

      <HandoverDialog
        open={handoverOpen}
        onClose={() => setHandoverOpen(false)}
        selection={selection}
        trainer={trainer}
        course={course}
        batch={batch}
        trainers={trainers}
        onDone={loadRows}
        notify={notify}
        onError={reportError}
      />

      <GuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />

      <Dialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogHeader
          title="Discard unsaved changes?"
          onClose={() => setPendingAction(null)}
        />
        <DialogContent>
          <Typography>
            You have edited rows that are not saved. Continuing will discard
            those edits.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setPendingAction(null)}>
            Keep editing
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const action = pendingAction;
              setPendingAction(null);
              if (action) action();
            }}
          >
            Discard and continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default CourseTracker;